import { ControlModeTemplateFieldsType } from '@src/types/ControlModes';
import {
  CombinedControlModeField,
  ControlModes,
  ISections,
} from '@src/types/Stacks';

interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
  nameError?: string;
}

interface DuplicateNameValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

export const validateDuplicateControlModeNames = (
  controlModes: ControlModes[],
): DuplicateNameValidationResult => {
  let isValid = true;
  const errors: Record<string, string> = {};

  // Create a map of control mode names to their indices
  const controlModeNames = new Map<string, number[]>();

  // Populate the map with control mode names and their indices
  controlModes.forEach((controlMode, index) => {
    const name = controlMode.name.trim();
    if (name) {
      if (!controlModeNames.has(name)) {
        controlModeNames.set(name, [index]);
      } else {
        controlModeNames.get(name)?.push(index);
      }
    }
  });

  // Add validation errors for duplicate control mode names
  controlModeNames.forEach((indices) => {
    if (indices.length > 1) {
      indices.forEach((index) => {
        errors[`controlMode_${index}_name`] = 'Duplicate Control Mode Name';
        isValid = false;
      });
    }
  });

  return { isValid, errors };
};

export const validateStackForm = (
  stackName: string,
  controlModes: ControlModes[],
): ValidationResult => {
  let isValid = true;
  const errors: Record<string, string> = {};
  let nameError: string | undefined;

  // Validate stack name
  if (!stackName.trim()) {
    nameError = 'Stack name is required';
    isValid = false;
  }

  // Validate that at least one control mode exists
  if (controlModes.length === 0) {
    isValid = false;
  }

  // Validate for duplicate control mode names
  const duplicateNameValidation =
    validateDuplicateControlModeNames(controlModes);

  // Merge validation results
  if (!duplicateNameValidation.isValid) {
    isValid = false;
    Object.assign(errors, duplicateNameValidation.errors);
  }

  // Validate each control mode
  controlModes.forEach((controlMode, index) => {
    // Validate control mode name
    if (!controlMode.name.trim()) {
      errors[`controlMode_${index}_name`] = 'Control mode name is required';
      isValid = false;
    }

    // Validate that enabled control modes have sections
    if (
      controlMode.enabled &&
      (!controlMode.sections || controlMode.sections.length === 0)
    ) {
      errors[`controlMode_${index}_sections`] =
        'Enabled control modes must have at least one section';
      isValid = false;
    }

    // Validate each section and field
    controlMode.sections?.forEach(
      (section: ISections, sectionIndex: number) => {
        section.fields.forEach(
          (field: CombinedControlModeField, fieldIndex: number) => {
            const fieldId = `controlMode_${index}_section_${sectionIndex}_field_${fieldIndex}`;

            // Validate based on field type
            switch (field.type) {
              case ControlModeTemplateFieldsType.NUMBER:
                if ('range' in field && field.range) {
                  const value = Number(field.value);
                  if (isNaN(value)) {
                    errors[fieldId] = 'Must be a number';
                    isValid = false;
                  } else if (
                    (field.range.min !== undefined &&
                      value < field.range.min) ||
                    (field.range.max !== undefined && value > field.range.max)
                  ) {
                    errors[fieldId] =
                      `Must be between ${field.range.min ?? 'min'} and ${field.range.max ?? 'max'}`;
                    isValid = false;
                  }
                }
                break;
              case ControlModeTemplateFieldsType.SELECT:
                if (
                  'listOptions' in field &&
                  field.listOptions &&
                  !field.listOptions.includes(field.value as string)
                ) {
                  errors[fieldId] = 'Invalid selection';
                  isValid = false;
                }
                break;
            }
          },
        );
      },
    );
  });

  return { isValid, errors, nameError };
};
